/**
 * PhotoRetagModal — Rescue tool for mis-tagged inspection photos.
 *
 * Opens from the Admin job detail. Loads every photo on the job (from
 * before_photos + after_photos), groups them by their current room tag,
 * and lets an admin tap photos to select them, then tap a target room
 * chip to bulk-retag. Uses POST /api/jobs/{id}/photos/retag-bulk which
 * is now bucket-aware — it MOVES photos between before/after buckets
 * automatically based on the target phase.
 *
 * Ideal use case: a tech shot all 19 photos while still on room 1 in
 * a periodic inspection — an admin opens this, taps photos in bulk,
 * assigns them to their real rooms in under a minute, and the PDF
 * regenerates correctly on next open.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  X, ArrowsClockwise, CheckCircle, Warning, Sparkle, MapPin,
  Selection, ArrowRight, Info,
} from "@phosphor-icons/react";

const UNTAGGED = "__UNTAGGED__";

export default function PhotoRetagModal({ jobId, onClose, onDone }) {
  const [job, setJob]       = useState(null);
  const [loading, setLoad]  = useState(true);
  const [selected, setSel]  = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState(""); // "" | room name | UNTAGGED

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const { data } = await api.get(`/jobs/${jobId}`);
      setJob(data);
    } catch (e) {
      toast.error("Failed to load job");
    } finally { setLoad(false); }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  // Combine all photos into a flat list with a bucket label + per-job overlay tag
  const jobLabel = useMemo(() => {
    if (!job) return "";
    if (job.is_turn) return null; // show OUT/IN per bucket
    const t = String(job.inspection_type || "").toLowerCase();
    if (t === "periodic") return "PERIODIC";
    if (t === "move_in")  return "MOVE-IN";
    if (t === "move_out") return "MOVE-OUT";
    return null;
  }, [job]);
  const photos = useMemo(() => {
    if (!job) return [];
    const flat = [];
    for (const b of ["before_photos", "after_photos"]) {
      for (const p of (job[b] || [])) {
        if (!p || p.media_type === "video") continue;
        flat.push({ ...p, __bucket: b });
      }
    }
    return flat;
  }, [job]);

  const rooms = job?.rooms || [];

  // Target phase — periodic jobs use "periodic", turn/move_in/move_out use their type
  const targetPhase = useMemo(() => {
    if (!job) return "move_in";
    if (job.is_turn) return null; // ambiguous — admin must pick
    const t = String(job.inspection_type || "").toLowerCase();
    if (t === "move_out") return "move_out";
    if (t === "move_in")  return "move_in";
    if (t === "periodic") return "periodic";
    return "move_in";
  }, [job]);

  const [turnPhase, setTurnPhase] = useState("move_out"); // for turn jobs only
  const phase = job?.is_turn ? turnPhase : targetPhase;

  // Group by current room for the display grid
  const groups = useMemo(() => {
    const acc = {};
    for (const p of photos) {
      const k = p.inspection_room || UNTAGGED;
      (acc[k] ||= []).push(p);
    }
    // Sort: untagged last, others alphabetical
    const keys = Object.keys(acc).sort((a, b) => {
      if (a === UNTAGGED) return 1;
      if (b === UNTAGGED) return -1;
      return a.localeCompare(b);
    });
    return keys.map(k => ({ room: k, items: acc[k] }));
  }, [photos]);

  const toggleOne = (path) => {
    setSel(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };
  const selectGroup = (items) => {
    const paths = items.map(x => x.path);
    setSel(prev => {
      const next = new Set(prev);
      const allSelected = paths.every(p => next.has(p));
      if (allSelected) paths.forEach(p => next.delete(p));
      else paths.forEach(p => next.add(p));
      return next;
    });
  };
  const clearSel = () => setSel(new Set());

  const applyToRoom = async (room) => {
    if (selected.size === 0) { toast.error("Select at least one photo"); return; }
    if (!phase) { toast.error("Pick move-out or move-in for this turn first"); return; }
    setSaving(true);
    try {
      await api.post(`/jobs/${jobId}/photos/retag-bulk`, {
        paths: Array.from(selected),
        room,
        phase,
      });
      toast.success(`Moved ${selected.size} photo${selected.size === 1 ? "" : "s"} to ${room}`);
      clearSel();
      await load();
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Retag failed"));
    } finally { setSaving(false); }
  };

  const shownGroups = useMemo(() => {
    if (!filter) return groups;
    return groups.filter(g => g.room === filter);
  }, [groups, filter]);

  const totalPhotos = photos.length;
  const untaggedCount = groups.find(g => g.room === UNTAGGED)?.items.length || 0;

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/70 flex items-start sm:items-center justify-center p-2 sm:p-4"
      data-testid="photo-retag-modal">
      <div className="bg-white w-full max-w-5xl max-h-[95vh] flex flex-col border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] overflow-hidden">
        <div className="p-3 bg-gradient-to-r from-fuchsia-600 to-red-600 text-white flex items-start justify-between gap-2 sticky top-0">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80 flex items-center gap-1">
              <Sparkle weight="fill" size={11}/> Rescue Tool
            </div>
            <div className="font-black uppercase tracking-widest">Fix Photo Rooms</div>
            <div className="text-[11px] opacity-90 mt-0.5">
              {job?.property_address}{job?.unit ? ` · ${job.unit}` : ""} · {totalPhotos} photos
              {untaggedCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-400 text-black text-[9px] font-black">
                  {untaggedCount} UNTAGGED
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} data-testid="retag-close" className="p-1 hover:bg-black/20">
            <X size={20}/>
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading photos…</div>
        ) : photos.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No inspection photos on this job.</div>
        ) : (
          <>
            {/* Help banner */}
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900 flex gap-2 items-start">
              <Info size={14} weight="fill" className="text-amber-600 shrink-0 mt-0.5"/>
              <div>
                <b>How it works:</b> Tap photos to select them (or the group header to select all in a room),
                then tap the target room chip below. Photos are moved instantly and the report will re-render on next open.
              </div>
            </div>

            {/* Turn-phase picker (only for turn jobs) */}
            {job?.is_turn && (
              <div className="px-4 py-2 bg-slate-100 border-b border-slate-200 flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Target phase:</span>
                <button onClick={() => setTurnPhase("move_out")}
                  className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${turnPhase === "move_out" ? "bg-red-600 text-white" : "bg-white text-slate-700 border border-slate-300"}`}
                  data-testid="retag-phase-out">
                  Move-Out
                </button>
                <button onClick={() => setTurnPhase("move_in")}
                  className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${turnPhase === "move_in" ? "bg-emerald-600 text-white" : "bg-white text-slate-700 border border-slate-300"}`}
                  data-testid="retag-phase-in">
                  Move-In
                </button>
              </div>
            )}

            {/* Room chips (target selector) — sticky under the header */}
            <div className="px-3 py-2 border-b-2 border-slate-200 bg-slate-50 sticky top-[70px] z-10">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 flex items-center justify-between">
                <span>Move selected {selected.size > 0 ? `(${selected.size})` : ""} to:</span>
                {selected.size > 0 && (
                  <button onClick={clearSel} className="text-[10px] text-slate-500 hover:text-slate-800 underline">Clear</button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {rooms.map(r => (
                  <button key={r} onClick={() => applyToRoom(r)} disabled={saving || selected.size === 0}
                    data-testid={`retag-to-${r}`}
                    className="px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest bg-white border-2 border-slate-300 hover:bg-fuchsia-600 hover:border-fuchsia-600 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    {r}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                <button onClick={() => setFilter("")}
                  className={`text-[10px] font-bold px-2 py-0.5 rounded ${filter === "" ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-200"}`}>
                  Show all
                </button>
                {groups.map(g => (
                  <button key={g.room} onClick={() => setFilter(filter === g.room ? "" : g.room)}
                    className={`text-[10px] font-bold px-2 py-0.5 rounded ${filter === g.room ? "bg-slate-800 text-white" : "text-slate-500 hover:bg-slate-200"}`}>
                    {g.room === UNTAGGED ? "Untagged" : g.room} ({g.items.length})
                  </button>
                ))}
              </div>
            </div>

            {/* Photo grid, grouped */}
            <div className="flex-1 overflow-y-auto p-3 space-y-4">
              {shownGroups.map(g => {
                const isUntagged = g.room === UNTAGGED;
                const allSelected = g.items.every(p => selected.has(p.path));
                return (
                  <div key={g.room} className="rounded-xl bg-slate-50 p-2 border-2 border-slate-200"
                    data-testid={`retag-group-${g.room}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                          isUntagged
                            ? "bg-amber-200 text-amber-900"
                            : "bg-cyan-100 text-cyan-900"}`}>
                          <MapPin weight="fill" size={10}/>
                          {isUntagged ? "Untagged" : g.room}
                        </span>
                        <span className="text-[10px] text-slate-500">{g.items.length} photo{g.items.length === 1 ? "" : "s"}</span>
                      </div>
                      <button onClick={() => selectGroup(g.items)}
                        data-testid={`retag-selectall-${g.room}`}
                        className="text-[10px] font-bold text-fuchsia-600 hover:text-fuchsia-800 flex items-center gap-1">
                        <Selection size={11} weight="fill"/> {allSelected ? "Deselect all" : "Select all"}
                      </button>
                    </div>
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
                      {g.items.map(p => {
                        const isSel = selected.has(p.path);
                        return (
                          <button key={p.path} onClick={() => toggleOne(p.path)}
                            data-testid={`retag-photo-${p.path}`}
                            className={`relative aspect-square overflow-hidden rounded-lg border-4 transition-all ${
                              isSel ? "border-fuchsia-600 ring-4 ring-fuchsia-300 scale-95" : "border-white hover:border-fuchsia-300"
                            }`}>
                            <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover"/>
                            {isSel && (
                              <div className="absolute inset-0 bg-fuchsia-600/40 flex items-center justify-center">
                                <CheckCircle size={28} weight="fill" className="text-white drop-shadow-lg"/>
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 text-[8px] text-white bg-black/60 py-0.5 text-center font-bold uppercase">
                              {jobLabel || (p.__bucket === "before_photos" ? "OUT" : "IN")}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer status */}
            <div className="p-2 border-t-2 border-slate-200 bg-slate-100 flex items-center justify-between">
              <div className="text-xs text-slate-600">
                <b>{selected.size}</b> selected of <b>{totalPhotos}</b> total
                {job?.is_turn && <span className="ml-2 opacity-70">Phase: <b>{turnPhase.replace("_", "-")}</b></span>}
              </div>
              <button onClick={load} disabled={saving}
                data-testid="retag-reload"
                className="text-[10px] font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1">
                <ArrowsClockwise size={11}/> Reload
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
